<!-- components/ChatWindow.vue -->
<template>
  <n-scrollbar class="chat-window" ref="scrollbar">
    <div class="messages-container">
      <!-- 空状态 -->
      <div v-if="messages.length === 0 && !loading" class="empty-state">
        <n-text type="info" style="font-size: 18px;">
          欢迎使用 鲸小小AI助教 聊天～<br />开始输入消息吧！
        </n-text>
      </div>

      <!-- 消息列表 -->
      <template v-for="msg in messages" :key="msg.id">
        <div :class="['message-wrapper', msg.role]">
          <!-- AI 头像 -->
          <n-avatar
              v-if="msg.role === 'assistant'"
              round
              size="medium"
              :src="aiAvatar"
              object-fit="cover"
          />

          <!-- 用户头像 -->
          <n-avatar
              v-else
              round
              size="medium"
              :src="userAvatar"
              object-fit="cover"
          />

          <div class="bubble" v-html="renderedContent(msg)"></div>

          <!-- 流式光标（放在 bubble 外，避免被 Markdown 解析） -->
<!--          <span v-if="msg.isStreaming" class="streaming-cursor">|</span>-->
        </div>
      </template>

      <!-- 正在生成 -->
      <div v-if="loading" class="message-wrapper assistant">
        <n-avatar
            round
            size="medium"
            :src="aiAvatar"
            object-fit="cover"
        />
        <div class="bubble">
          <n-spin size="small" />
          <span style="margin-left: 8px; color: #888;">思考中...</span>
        </div>
      </div>
    </div>
  </n-scrollbar>
</template>

<script setup>
import { watch, nextTick, ref, computed } from 'vue'
import { useChat } from '@/composables/useChat'
import { renderMarkdown } from '@/utils/markdown'
import { NScrollbar, NAvatar, NSpin, NText } from 'naive-ui'

import userAvatar from '@/assets/user.png'
import aiAvatar from '@/assets/ai.png'

const { messages, loading } = useChat()
const scrollbar = ref(null)

// 实时渲染 Markdown
const renderedContent = computed(() => (msg) => {
  // 用户消息直接显示纯文本（安全），助手消息渲染 Markdown
  if (msg.role === 'user') {
    return msg.content.replace(/\n/g, '<br>')
  }
  return renderMarkdown(msg.content)
})

// 自动滚动
watch(
    () => messages.length + (loading.value ? 1 : 0),
    () => {
      nextTick(() => {
        scrollbar.value?.scrollTo({ position: 'bottom', force: true })
      })
    },
    { immediate: true }
)
</script>
<style scoped>
/* 主容器 */
.chat-window {
  height: 100%;
  padding: 20px 20px 40px;
}

/* 消息容器 */
.messages-container {
  min-height: 80vh;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: flex-start;
}

/* 空状态 */
.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  text-align: center;
  color: #999;
}

/* 消息行 */
.message-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.message-wrapper.assistant { flex-direction: row; }
.message-wrapper.user { flex-direction: row-reverse; }

/* 头像 */
.n-avatar {
  flex-shrink: 0;
}

.n-avatar :deep(img) {
  object-fit: cover !important;
  width: 100%;
  height: 100%;
}

/* 气泡基础样式 */
.bubble {
  max-width: 80%;
  width: fit-content;
  padding: 12px 16px;
  border-radius: 18px;
  background: #f5f5f5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  word-break: break-word;
  line-height: 1.5;
  align-self: flex-start;
}

.user .bubble {
  background: #409eff;
  color: white;
  align-self: flex-end;
  border-bottom-right-radius: 4px;
}

.assistant .bubble {
  border-bottom-left-radius: 4px;
}

/* 长内容支持横向滚动 */
.bubble :deep(pre),
.bubble :deep(table),
.bubble :deep(blockquote),
.bubble :deep(.katex-display) {
  max-width: 100%;
  overflow-x: auto;
}

/* ================== Grok 亮色代码块风格（重点）================== */
.bubble :deep(pre) {
  background: #f6f8fa !important;     /* Grok 同款浅灰背景 */
  color: #24292e;                     /* 深灰文字，高对比度 */
  padding: 16px;
  border-radius: 12px;
  margin: 16px 0;
  font-size: 14px;
  line-height: 1.6;
  border: 1px solid #e1e4e8;           /* 柔和边框 */
  overflow-x: auto;
  position: relative;
}

/* Grok 风格高亮（使用 github 亮主题） */
.bubble :deep(.hljs) {
  background: transparent !important;
}

/* 语言标签（右上角，像 Grok 那样简洁） */
.bubble :deep(pre code)::before {
  content: attr(data-language);
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 11px;
  color: #586069;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

/* 内联代码 */
.bubble :deep(code:not(pre code)) {
  background: rgba(0, 0, 0, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
}

/* 用户气泡中的代码块（蓝色背景）也用亮色，避免看不清 */
.user .bubble :deep(pre) {
  background: #e6f1ff !important;     /* 浅蓝灰背景，清晰易读 */
  border: 1px solid #91c9ff;
  color: #1e1e1e;
}

.user .bubble :deep(code:not(pre code)) {
  background: rgba(255, 255, 255, 0.3);
}

/* KaTeX 公式 */
.bubble :deep(.katex-display) {
  padding: 12px 0;
  margin: 16px 0;
}

.bubble :deep(.katex) {
  font-size: 1.1em !important;
}

/* 流式光标 */
.streaming-cursor {
  animation: blink 1s infinite;
  margin-left: 8px;
  font-weight: bold;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
</style>